/**
 * 🧪 SIMPLE VERIFICATION TEST
 * Basic test page to ensure the admin verification system loads without errors
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Badge
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Visibility,
  Camera,
  Person,
  Description,
  VerifiedUser
} from '@mui/icons-material';

// Create sample document images using canvas (guaranteed to work)
const createSampleImage = (text: string, bgColor: string): string => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 300;
  const ctx = canvas.getContext('2d');
  
  if (ctx) {
    // Background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, 400, 300);
    
    // Border
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, 380, 280);
    
    // Text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(text, 200, 160);
    
    // Document ID
    ctx.font = '16px Arial';
    ctx.fillText('DEMO DOCUMENT', 200, 200);
    ctx.fillText('FOR VERIFICATION', 200, 220);
    
    return canvas.toDataURL();
  }
  
  // Fallback if canvas fails
  return 'data:image/svg+xml;base64,' + btoa(`
    <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="300" fill="${bgColor}"/>
      <text x="200" y="150" text-anchor="middle" fill="white" font-size="24" font-family="Arial">${text}</text>
      <text x="200" y="200" text-anchor="middle" fill="white" font-size="16" font-family="Arial">DEMO DOCUMENT</text>
    </svg>
  `);
};

// Mock data with guaranteed working images
const mockRequests = [
  {
    verificationId: 'UPLOAD_1234567890_abc123',
    userId: 'user-001',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    userPhone: '+2348012345678',
    documentType: 'passport',
    status: 'pending_review' as const,
    submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    imageUrls: [
      createSampleImage('PASSPORT FRONT', '#2196F3'),
      createSampleImage('SELFIE PHOTO', '#4CAF50')
    ],
    documentData: {
      fullName: 'John Doe',
      passportNumber: 'A12345678',
      expiryDate: '2030-12-31'
    }
  },
  {
    verificationId: 'UPLOAD_2345678901_def456',
    userId: 'user-002', 
    userName: 'Jane Smith',
    userEmail: 'jane@example.com',
    documentType: 'national_id',
    status: 'pending_review' as const,
    submittedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    imageUrls: [
      createSampleImage('NATIONAL ID FRONT', '#FF9800'),
      createSampleImage('SELFIE PHOTO', '#9C27B0')
    ]
  }
];

const SimpleVerificationTest: React.FC = () => {
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      verified: 'success',
      rejected: 'error'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      pending_review: <Schedule />,
      verified: <CheckCircle />,
      rejected: <Cancel />
    };
    return icons[status as keyof typeof icons];
  };

  const getDocumentTypeIcon = (docType: string) => {
    const icons = {
      passport: <Description />,
      national_id: <Person />,
      driver_license: <VerifiedUser />,
      selfie: <Camera />
    };
    return icons[docType as keyof typeof icons] || <Description />;
  };

  const stats = {
    total: mockRequests.length,
    pending: mockRequests.filter(r => r.status === 'pending_review').length,
    verified: mockRequests.filter(r => r.status === 'verified').length,
    rejected: mockRequests.filter(r => r.status === 'rejected').length
  };

  const getSuccessRate = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.verified / (stats.verified + stats.rejected)) * 100) || 0;
  };

  return (
    <Box p={3}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="h6" component="div" gutterBottom>
          ✅ Admin Verification System Test - WORKING!
        </Typography>
        <Typography variant="body2">
          This is a simplified version of the admin verification system to test basic functionality.
          All components are loading correctly and mock data is displaying properly.
        </Typography>
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Requests
                  </Typography>
                  <Typography variant="h4">
                    {stats.total}
                  </Typography>
                </Box>
                <VerifiedUser color="primary" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Review
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <Schedule color="warning" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Verified
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.verified}
                  </Typography>
                  <Chip 
                    label={`${getSuccessRate()}% success rate`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Rejected
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.rejected}
                  </Typography>
                </Box>
                <Cancel color="error" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Verification Requests Table */}
      <Paper elevation={2}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Verification Requests
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockRequests.map((request) => (
                  <TableRow key={request.verificationId}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {request.userName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {request.userName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.userEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getDocumentTypeIcon(request.documentType)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {request.documentType.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(request.submittedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(request.submittedAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Badge badgeContent={request.imageUrls.length} color="primary">
                        <Camera fontSize="small" />
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Button 
                          size="small" 
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => setSelectedRequest(request)}
                        >
                          View
                        </Button>
                        <Button 
                          size="small" 
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="small" 
                          variant="contained"
                          color="error"
                          startIcon={<Cancel />}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {selectedRequest && (
        <Paper elevation={3} sx={{ mt: 3, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              📄 Document Viewer - {selectedRequest.userName}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setSelectedRequest(null)}
            >
              Close
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                User Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1">{selectedRequest.userName}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{selectedRequest.userEmail}</Typography>
              </Box>
              {selectedRequest.userPhone && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{selectedRequest.userPhone}</Typography>
                </Box>
              )}
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Document Type</Typography>
                <Typography variant="body1">{selectedRequest.documentType.replace('_', ' ').toUpperCase()}</Typography>
              </Box>
              {selectedRequest.documentData && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Extracted Data</Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                      {JSON.stringify(selectedRequest.documentData, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Document Images ({selectedRequest.imageUrls.length})
              </Typography>
              <Grid container spacing={1}>
                {selectedRequest.imageUrls.map((imageUrl: string, index: number) => (
                  <Grid item xs={6} key={index}>
                    <Card>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`Document ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          // Create a simple fallback image
                          const fallbackSvg = `data:image/svg+xml;base64,${btoa(`
                            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                              <rect width="400" height="300" fill="#E0E0E0"/>
                              <text x="200" y="140" text-anchor="middle" fill="#757575" font-size="18" font-family="Arial">DOCUMENT IMAGE</text>
                              <text x="200" y="170" text-anchor="middle" fill="#757575" font-size="14" font-family="Arial">Image ${index + 1}</text>
                            </svg>
                          `)}`;
                          e.currentTarget.src = fallbackSvg;
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box mt={2} display="flex" gap={1}>
                <Button 
                  variant="contained" 
                  color="success" 
                  size="small"
                  startIcon={<CheckCircle />}
                >
                  Approve
                </Button>
                <Button 
                  variant="contained" 
                  color="error" 
                  size="small"
                  startIcon={<Cancel />}
                >
                  Reject
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default SimpleVerificationTest;